import { useParams } from 'react-router-dom'
import { buildErrorMessage, ifcRootGuid } from '@constants'
import { AxiosError } from 'axios'
import { useControlStore } from '@editor/stores'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { getProjectFiles } from '@queries'
import { uploadIFCFile } from '@mutations'
import { retryUntilSuccessOrFailure } from 'src/state/utils'
import useNotification from '../useNotification'

const useUploadIFCFile = (
  onUploadSuccess?: (data: { task_id: string; ifc_file_guid: string }) => void,
) => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()

  const queryClient = useQueryClient()

  const setShowGltfModel = useControlStore(state => state.setShowGltfModel)
  const setActiveIfcFileGuid = useControlStore(state => state.setActiveIfcFileGuid)

  const { triggerNotification } = useNotification()

  return useMutation(
    async (file: File) => {
      const data = await uploadIFCFile.request(projectId!, file)

      await retryUntilSuccessOrFailure({
        // @ts-ignore
        url: `/task-status/${data?.task_id}`,
      })

      return data
    },
    {
      mutationKey: ['uploadIFCFile', projectId],
      meta: {
        title: 'IFC-Datei hochladen & konvertieren',
        description: 'Lädt eine IFC-Datei hoch und konvertiert sie auf dem Server.',
      },
      onSuccess: data => {
        setShowGltfModel(true)
        // Make the freshly uploaded file the active IFC file.
        if (data?.ifc_file_guid) setActiveIfcFileGuid(data.ifc_file_guid)
        queryClient.invalidateQueries(getProjectFiles.getKey(projectId!, ifcRootGuid))
        enqueueSnackbar('IFC-Datei erfolgreich hochgeladen & konvertiert.', {
          variant: 'success',
        })
        triggerNotification({ message: 'IFC-Datei erfolgreich hochgeladen & konvertiert.' })
        onUploadSuccess?.(data)
      },
      onError: error => {
        enqueueSnackbar(
          buildErrorMessage(error as unknown as AxiosError, 'Fehler beim Hochladen der IFC-Datei'),
          { variant: 'error' },
        )
      },
    },
  )
}

export default useUploadIFCFile
